//
// Copyright (C) 2012 - present Instructure, Inc.
//
// This file is part of Canvas.
//
// Canvas is free software: you can redistribute it and/or modify it under
// the terms of the GNU Affero General Public License as published by the Free
// Software Foundation, version 3 of the License.
//
// Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
// WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
// A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
// details.
//
// You should have received a copy of the GNU Affero General Public License along
// with this program. If not, see <http://www.gnu.org/licenses/>.

// #
// add the [data-tooltip] attribute and title="<tooltip contents>" to anything you want to give a tooltip:
//
// usage: (see Styleguide)
//   <a data-tooltip title="pops up on top center">default</a>
//   <a data-tooltip="top" title="same as default">top</a>
//   <a data-tooltip="right" title="should be right center">right</a>
//   <a data-tooltip="bottom" title="should be bottom center">bottom</a>
//   <a data-tooltip="left" title="should be left center">left</a>
//   <a data-tooltip='{"track":true}' title="this toolstip will stay connected to mouse as it moves around">
//     tooltip that tracks mouse
//   </a>
//   <button data-tooltip title="any type of element can have a tooltip" class="btn">
//     button with tooltip
//   </button>

import $ from 'jquery'
import htmlEscape, {raw} from '@instructure/html-escape'
import {sanitizeHTML} from '@canvas/sanitize-html'
import 'jqueryui/tooltip'

// Every jQuery UI tooltip funnels through `_open(event, target, content)`,
// which sets `content` as innerHTML on `.ui-tooltip-content`. Patching the
// prototype once runs DOMPurify on every content path (title attr,
// data-html-tooltip-title, custom content callbacks, direct `.tooltip()`
// callers) before it reaches the sink.
const baseOpen = $.ui.tooltip.prototype._open
$.ui.tooltip.prototype._open = function (event, target, content) {
  return baseOpen.call(
    this,
    event,
    target,
    typeof content === 'string' ? sanitizeHTML(content) : content,
  )
}

const tooltipsToShortCirtuit = {}
const shortCircutTooltip = target =>
  tooltipsToShortCirtuit[target] || tooltipsToShortCirtuit[target[0]]

// Grace period before a mouse-out actually closes the tooltip, so the pointer
// can travel across the caret gap onto the tooltip (or back to the trigger)
// without it vanishing -- WCAG 1.4.13 "hoverable". Kept generous so users with
// motor impairments have time to make the crossing.
const TOOLTIP_HOVER_GRACE = 500

const tooltipUtils = {
  setPosition(opts) {
    function caret() {
      if ((opts.tooltipClass || '').match('popover')) {
        return 30
      } else {
        return 5
      }
    }

    const collision = opts.force_position === 'true' ? 'none' : 'flipfit'
    const positions = Object.assign(Object.create(null), {
      right: {
        my: 'left center',
        at: `right+${caret()} center`,
        collision,
      },
      left: {
        my: 'right center',
        at: `left-${caret()} center`,
        collision,
      },
      top: {
        my: 'center bottom',
        at: `center top-${caret()}`,
        collision,
      },
      bottom: {
        my: 'center top',
        at: `center bottom+${caret()}`,
        collision,
      },
    })
    // Object.hasOwn (not the `in` operator) against a null-prototype lookup
    // table: `in` walks the prototype chain, so a plain {} lookup table
    // would let position: "__proto__"/"constructor" resolve to
    // Object.prototype/Object and pollute every object on the page via the
    // assignments below.
    if (typeof opts.position === 'string' && Object.hasOwn(positions, opts.position)) {
      opts.position = positions[opts.position]
    } else if (opts.position && typeof opts.position === 'object') {
      const allowed = {}
      if (typeof opts.position.my === 'string') allowed.my = opts.position.my
      if (typeof opts.position.at === 'string') allowed.at = opts.position.at
      if (typeof opts.position.collision === 'string') allowed.collision = opts.position.collision
      opts.position = allowed
    }
  },
}

// create a custom widget that inherits from the default jQuery UI
// tooltip but extends the open method with a setTimeout wrapper so
// that our browser can scroll to the tabbed focus element before
// positioning the tooltip relative to window.
$.widget('custom.timeoutTooltip', $.ui.tooltip, {
  _open(event, target, content) {
    if (shortCircutTooltip(target)) return null

    // Converts arguments to an array
    const args = Array.prototype.slice.call(arguments, 0)
    args.splice(2, 1, htmlEscape(content).toString())
    // if you move very fast, it's possible that
    // @timeout will be defined
    if (this.timeout) return

    const apply = this._superApply.bind(this, args)
    this.timeout = setTimeout(() => {
      // make sure close will be called
      delete this.timeout
      // remove extra handlers we added, super will add them back
      this._off(target, 'mouseleave focusout keyup')
      apply()
      this._bindHoverable(target)
    }, 200)
    // this is from the jquery ui tooltip _open
    // we need to bind events to trigger close so that the
    // timeout is cleared when we mouseout / or leave focus
    return this._on(target, {
      mouseleave: 'close',
      focusout: 'close',
      keyup(event_) {
        if (event_.keyCode === $.ui.keyCode.ESCAPE) {
          const fakeEvent = $.Event(event_)
          fakeEvent.currentTarget = target[0]
          return this.close(fakeEvent, true)
        }
      },
    })
  },

  close(event) {
    if (this.timeout) {
      clearTimeout(this.timeout)
      delete this.timeout
      return
    }
    this._cancelClose()
    // A mouse-out (of the trigger or of the tooltip itself) closes on a delay
    // so the pointer can cross the caret gap onto the tooltip and keep it open.
    // Focus/scroll/click/Escape paths pass a non-mouseleave (or no) event and
    // still close immediately.
    if (event && event.type === 'mouseleave') {
      // _superApply is only valid synchronously inside this method, so bind it
      // now and defer the call (same trick as _open's timeout above).
      const closeNow = this._superApply.bind(this, [event, true])
      this.closeTimer = setTimeout(() => {
        delete this.closeTimer
        closeNow()
      }, TOOLTIP_HOVER_GRACE)
      return
    }
    return this._superApply([event, true])
  },

  _cancelClose() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
      delete this.closeTimer
    }
  },

  // Keep the tooltip open while the pointer is over it (or back on the
  // trigger); leaving the tooltip triggers the delayed close above.
  _bindHoverable(target) {
    const tooltip = this._find(target)
    if (!tooltip.length) return
    this._off(target, 'mouseenter')
    this._on(target, {mouseenter: '_cancelClose'})
    // Plain jQuery on the transient tooltip node: it's recreated each open and
    // removed on close, so `.remove()` cleans these up. Using this._on would
    // leak the detached node into the widget's `bindings` on every open.
    tooltip
      .on('mouseenter', () => this._cancelClose())
      .on('mouseleave', () => {
        const fakeEvent = $.Event('mouseleave')
        fakeEvent.currentTarget = target[0]
        this.close(fakeEvent)
      })
  },
})

// you can provide a 'using' option to jqueryUI position (which gets called by jqueryui Tooltip to
// position it on the screen), it will be passed the position cordinates and a feedback object which,
// among other things, tells you where it positioned it relative to the target. we use it to add some
// css classes that handle putting the pointer triangle (aka: caret) back to the trigger.
function using(position, feedback) {
  return $(this)
    .css(position)
    .removeClass('left right top bottom center middle vertical horizontal')
    .addClass(
      [
        // one of: "left", "right", "center"
        feedback.horizontal,

        // one of "top", "bottom", "middle"
        feedback.vertical,

        // if tooltip was positioned mostly above/below trigger then: "vertical"
        // else since the tooltip was positioned more to the left or right: "horizontal"
        feedback.important,
      ].join(' '),
    )
}

$('body').on('mouseenter focusin', '[data-tooltip]', function (_event) {
  const $this = $(this)
  let opts = $this.data('tooltip')

  // allow specifying position by simply doing <a data-tooltip="left">
  // and allow shorthand top|bottom|left|right positions like <a data-tooltip='{"position":"left"}'>
  if (['right', 'left', 'top', 'bottom'].includes(opts)) {
    opts = {position: opts}
  } else if (!opts || typeof opts !== 'object') {
    // Non-position strings are truthy; assigning .position on them throws.
    opts = {}
  }
  if (!opts.position) opts.position = 'top'

  tooltipUtils.setPosition(opts)
  if (opts.collision) opts.position.collision = opts.collision

  if (!opts.position.using) opts.position.using = using

  if ($this.data('html-tooltip-title')) {
    opts.content = function () {
      return raw($(this).data('html-tooltip-title'))
    }
    opts.items = '[data-html-tooltip-title]'
  }

  if ($this.data('tooltip-class')) opts.tooltipClass = $this.data('tooltip-class')

  $this
    .removeAttr('data-tooltip')
    .timeoutTooltip(opts)
    .timeoutTooltip('open')
    .click(() => {
      // Check if tooltip widget instance exists before calling methods
      if ($this.data('custom-timeoutTooltip')) {
        $this.timeoutTooltip('close')
      }
    })
})

// jQuery UI tooltips don't reposition on scroll.
function closeOpenTooltipsOnScroll() {
  const openTooltips = document.getElementsByClassName('ui-tooltip')
  if (!openTooltips.length) return
  // hide instantly so the fade-out doesn't linger at a stale position
  Array.from(openTooltips).forEach(el => {
    el.style.display = 'none'
  })
  $('[aria-describedby]').each(function () {
    const widget = $(this).data('custom-timeoutTooltip')
    if (widget && Object.keys(widget.tooltips || {}).length) {
      $(this).timeoutTooltip('close')
    }
  })
}

window.addEventListener('scroll', closeOpenTooltipsOnScroll, {capture: true, passive: true})

// The trigger-scoped keyup Escape (bound in the widget) only fires when the
// trigger is keyboard-focused. A mouse-only hover leaves the trigger unfocused,
// so dismiss open tooltips from a document-level Escape too -- WCAG 1.4.13
// "dismissible".
function closeOpenTooltipsOnEscape(event) {
  if (event.key !== 'Escape') return
  if (!document.getElementsByClassName('ui-tooltip').length) return
  $('[aria-describedby]').each(function () {
    const widget = $(this).data('custom-timeoutTooltip')
    if (widget && Object.keys(widget.tooltips || {}).length) {
      $(this).timeoutTooltip('close')
    }
  })
}

document.addEventListener('keydown', closeOpenTooltipsOnEscape, true)

const restartTooltip = event => (tooltipsToShortCirtuit[event.target] = false)

const stopTooltip = event => (tooltipsToShortCirtuit[event.target] = true)

$(window).bind('detachTooltip', stopTooltip)
$(window).bind('reattachTooltip', restartTooltip)

export default tooltipUtils
